import { Box, Colors } from '@be-brite/briteui';
import ReactMarkdown from 'react-markdown';
import { Text } from '../shared-components';
import { css } from 'emotion';

/**
 * Renders markdown content from assistant messages with custom styling.
 * @param {Object} message - The message object containing text to render.
 */
export const AssistantMarkdownRenderer = ({ message }) => {
  return (
    <Box
      css={`
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
      `}
    >
      <ReactMarkdown
        components={{
          // Render paragraphs with label styling.
          p: ({ node, ...props }) => <Text {...props} label />,
          // Render links conditionally based on whether they're citations
          a: ({ node, href, children, title, ...props }) => {
            // Check if this is a citation (has data-citation-id).
            const isCitation = title && title.includes('data-citation-id:');

            if (isCitation) {
              return (
                <AssistantMessageCitation href={href} title={title} {...props}>
                  {children}
                </AssistantMessageCitation>
              );
            }

            // Regular link.
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={css`
                  color: ${Colors.Purple};
                `}
              >
                {children}
              </a>
            );
          },
          // Style ordered lists with flex layout and spacing.
          ol: ({ node, ...props }) => (
            <ol
              {...props}
              ordered={props.ordered.toString()}
              className={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                justify-content: center;
                gap: 8px;
              `}
            />
          ),
          // Render list items with proper list styling.
          li: ({ node, ...props }) => {
            return (
              <Text
                {...props}
                ordered={props.ordered.toString()}
                as="span"
                label
                css={`
                  display: list-item;
                `}
              >
                {props.children}
              </Text>
            );
          },
        }}
        children={message.text}
      />
    </Box>
  );
};

/**
 * Citation component for assistant messages that renders clickable citation links.
 * Opens the citation URL in a new tab when clicked.
 */
const AssistantMessageCitation = ({ href, children, ...props }) => {
  // Handle citation click - opens URL in new tab with security attributes.
  const handleCitationClick = () => {
    if (!href) return;
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  return (
    <Text
      {...props}
      as="span"
      onClick={handleCitationClick}
      className={css`
        display: inline-flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        min-height: 16px;
        max-width: 100%;
        margin: 4px;
        padding: 4px 8px;
        border-radius: 16px;
        background: var(--Primary-Purple-400, #dfd6fd);
        cursor: pointer;
        &:hover {
          background: var(--Primary-Purple-500, #c7b8ff);
        }
      `}
    >
      <Text
        as="span"
        helper
        color="#845FFF"
        css={`
          white-space: normal;
          word-break: break-word;
          overflow-wrap: anywhere;
          text-align: left;
          width: 100%;
        `}
      >
        {children}
      </Text>
    </Text>
  );
};
